import { useEffect, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@tailoring-crm/ui';
import type { TourStepId } from './resolve-step.js';
import { findTourTarget, tourProgress, type TourStepDef } from './tour-steps.js';

/**
 * The "fill this in" half of the tour: a card that floats over a form without blocking it, and
 * a pulse on the button that finishes it.
 *
 * Outside a dialog it sits at the top of the screen on a phone (the thumb and the keyboard are
 * at the bottom) and at the bottom on a laptop. Inside one it has to be drawn INTO the dialog —
 * a modal `<dialog>` is in the top layer and makes everything outside it inert and hidden — so
 * it is portalled there and sticks to the dialog's foot, without buttons that would compete
 * with the dialog's own.
 */
export function TourHint({
  step,
  copyKey,
  def,
  dialog,
  onPause,
  onSkip,
}: {
  step: TourStepId;
  copyKey: string;
  def: TourStepDef;
  dialog: HTMLDialogElement | null;
  onPause: () => void;
  onSkip: () => void;
}): ReactElement | null {
  const { t } = useTranslation();
  usePulse(def.target);

  const progress = t('onboarding.spotlight.progress', tourProgress(step));
  const title = t(`onboarding.spotlight.steps.${copyKey}.title`);
  const body = t(`onboarding.spotlight.steps.${copyKey}.body`);

  if (def.inDialog) {
    if (!dialog) return null;
    return createPortal(
      <div
        role="status"
        className="sticky bottom-0 -mx-1 flex shrink-0 items-start gap-2 rounded-lg border border-primary/30 bg-background p-3 text-sm shadow-[0_-8px_16px_-8px_rgb(15_23_42/0.15)] animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none"
      >
        <Sparkles aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
        <p>
          <span className="font-semibold">{title}</span> {body}{' '}
          <span className="text-muted-foreground">({progress})</span>
        </p>
      </div>,
      dialog,
    );
  }

  return createPortal(
    <div
      role="status"
      // On a laptop it parks in the bottom-left corner, over the sidebar's section column —
      // the one part of the screen a form never uses.
      className="fixed inset-x-4 top-[calc(theme(spacing.header)+0.75rem)] z-40 mx-auto max-w-md rounded-xl border border-primary/30 bg-background p-4 shadow-xl animate-in fade-in-0 slide-in-from-top-4 duration-300 motion-reduce:animate-none md:bottom-4 md:left-3 md:right-auto md:top-auto md:mx-0 md:w-64 md:slide-in-from-bottom-4"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
        >
          <Sparkles className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">{progress}</p>
          <p className="font-semibold leading-snug">{title}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="ghost" onClick={onPause}>
              {t('onboarding.spotlight.later')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onSkip}>
              {t('onboarding.spotlight.skip')}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={onPause}
          aria-label={t('onboarding.spotlight.later')}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** Rings the step's finishing button — once it exists, and for as long as the step lasts. */
function usePulse(target: string | null): void {
  useEffect(() => {
    if (!target) return;
    let lit: HTMLElement | null = null;
    const apply = () => {
      const element = findTourTarget(target);
      if (element === lit) return;
      lit?.classList.remove('crm-tour-pulse');
      lit = element;
      lit?.classList.add('crm-tour-pulse');
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      lit?.classList.remove('crm-tour-pulse');
    };
  }, [target]);
}
